import type { CurrencyCode } from '@shared/hooks/useGeoLocale';
import { updateLocaleCurrency, useGeoBasedLocale } from '@shared/hooks/useGeoLocale';
import { asPrintSupportedCurrency } from '@shared/utils/currency/supportedCurrencies';

type PrintCurrency = Readonly<{
  /** Always a currency print can be sold in; unsupported values normalize to USD. */
  currency: CurrencyCode;
  /** Writes through to `joyLocale`. Permissive by design — see below. */
  setCurrency: (currency: CurrencyCode) => void;
}>;

/**
 * Stationery's selected currency: derived state over `joyLocale`, Joy's neutral locale store.
 *
 * Stores nothing of its own, so a currency changed elsewhere on Joy (registry, travel)
 * propagates here, and there is no private key that can go stale — contrast
 * `joyTravelCurrency`, which overrides `joyLocale` forever with no reset path.
 *
 * Normalization happens on read because the event-locale and browser-locale rungs of the
 * currency chain never pass through a setter. No `?currency=` handling of its own:
 * `useGeoBasedLocale` already reads the param at the top of its chain, and normalization
 * applies to it like every other rung.
 *
 * The setter is deliberately unrestricted: a write-side guard would not close the
 * event-locale path anyway, and a second gate could drift out of sync with
 * `PRINT_SUPPORTED_CURRENCIES`. Reads are normalized regardless of what was written.
 *
 * NOTE: `setCurrency` currently has no callers — `CurrencySelectorModal` writes by calling
 * `updateLocaleCurrency` directly and exposes no `onChange`. It is kept as the store's
 * documented write path for the pending `CurrencySelectorModal` fix. If that fix lands
 * without using it, delete it rather than leaving it unused.
 */
export const usePrintCurrency = (): PrintCurrency => {
  const { currency } = useGeoBasedLocale();

  return {
    currency: asPrintSupportedCurrency(currency),
    setCurrency: updateLocaleCurrency,
  };
};
